import {
    type VkMusicBundleCheckPossibilityQueryParams,
    VkMusicBundleCheckPossibilityAPICall,
} from 'reactApp/api/billing/vkmusic/VkMusicBundleCheckPossibilityAPICall';
import { vkMusicBundleLastPaymentDaysLimit } from 'reactApp/appHelpers/featuresHelpers/features/vkMusicBundle';
import { closeVkBuyModalWithSidebar } from 'reactApp/components/VkBuyModalWithSidebar/VkBuyModalWithSidebar.helpers';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { productsController } from 'reactApp/modules/products/products.controller';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { type RootState, store as reduxStore } from 'reactApp/store';
import { closeTariffBuyDialog } from 'reactApp/ui/TariffBuy/TariffBuy.helpers';
import type { VkMusicBundleModalProps } from 'reactApp/ui/VkMusic/VkMusicBundleModal/VkMusicBundleModal';
import { closeVkMusicBundleModal, renderVkMusicBundleModal } from 'reactApp/ui/VkMusic/VkMusicBundleModal/VkMusicBundleModal.helpers';
import { sendXray } from 'reactApp/utils/ga';
import { call, select } from 'typed-redux-saga';

import { loadBillingDataIfNotLoaded } from '../billingData/billingData.provider';
import { PaidInfoSelectors } from '../paidInfo/paidInfo.selectors';
import { type VK_MUSIC_BUNDLE_SOURCE, VK_MUSIC_PAY_SOURCE, vkMusicBundleLogger } from './vkMusic.const';
import { VkMusicBundleActions } from './vkMusic.module';
import type { VKIDInfo, VkMusicBundleTariffInfo } from './vkMusic.types';

const vkMusicBundleCheckPossibilityAPICall = (params: VkMusicBundleCheckPossibilityQueryParams) =>
    new VkMusicBundleCheckPossibilityAPICall().makeRequest(params);

export const enum CheckPossibilityStatus {
    TRIAL = 'TRIAL',
    USUAL = 'USUAL',
    NOT_AVAILABLE = 'NOT_AVAILABLE',
    ERROR = 'ERROR',
}

const checkVkMusicPossibilityNotAvailableCodes = [1310, 1871];

/**
 * Проверяет, какой тариф доступен для переданного вкид со стороны Музыки
 * @param {number} vkidLogin – вкид для проверки
 * @returns {CheckPossibilityStatus} – результат проверки
 */
export const checkVkMusicPossibility = async (vkidLogin: number) => {
    try {
        const { data } = await vkMusicBundleCheckPossibilityAPICall({ vkId: vkidLogin });
        const checkPossibilityResult = data || {};

        const { success, data: checkPossibilityData, error } = checkPossibilityResult;

        if (!success) {
            vkMusicBundleLogger.info('check-possibility error', checkPossibilityResult);
            sendXray(['check_possibility_error', error && String(error.code)]);
            if (checkVkMusicPossibilityNotAvailableCodes.includes(error?.code || 0)) {
                return CheckPossibilityStatus.NOT_AVAILABLE;
            }

            return CheckPossibilityStatus.ERROR;
        }
        vkMusicBundleLogger.info('check-possibility success: ', checkPossibilityResult);

        const { payable_subscription_available: usualTariffAvailable = false, trial_subscription_available: trialTariffAvailable = false } =
            checkPossibilityData || {};

        if (trialTariffAvailable) {
            return CheckPossibilityStatus.TRIAL;
        }
        if (usualTariffAvailable) {
            return CheckPossibilityStatus.USUAL;
        }
        return CheckPossibilityStatus.ERROR;
    } catch (error) {
        vkMusicBundleLogger.info('check-possibility error: ', error);
        sendXray('check_possibility_error');
        return CheckPossibilityStatus.ERROR;
    }
};

/**
 * Проверяет, какой тариф доступен активному юзеру со стороны Mail
 * @returns {CheckPossibilityStatus} – результат проверки
 */
export function* checkMailSpacePossibility() {
    // для проверки условия на последнюю оплату
    const success = yield loadBillingDataIfNotLoaded();
    if (!success) {
        return CheckPossibilityStatus.USUAL;
    }

    const isUserWithoutPayment = PaidInfoSelectors.isUserWithoutPayment(reduxStore.getState());

    if (isUserWithoutPayment) {
        return CheckPossibilityStatus.TRIAL;
    }

    const isNotLastPaymentLimit = yield* select((state: RootState) =>
        PaidInfoSelectors.lastPaymentWasLessThan(state, { days: vkMusicBundleLastPaymentDaysLimit })
    );

    if (!isNotLastPaymentLimit) {
        return CheckPossibilityStatus.TRIAL;
    }

    return CheckPossibilityStatus.USUAL;
}

export function* checkPossibility(vkid?: VKIDInfo) {
    const mailSpaceCheckStatus = yield* checkMailSpacePossibility();
    if ([CheckPossibilityStatus.ERROR, CheckPossibilityStatus.NOT_AVAILABLE].includes(mailSpaceCheckStatus)) {
        return { mailSpaceCheckStatus, vkMusicCheckStatus: CheckPossibilityStatus.TRIAL };
    }

    if (!vkid) {
        // если не можем проверить на стороне Музыки, считаем, что доступен триал – потом извинимся, если нет
        return { mailSpaceCheckStatus, vkMusicCheckStatus: CheckPossibilityStatus.TRIAL };
    }

    const vkMusicCheckStatus = yield* call(checkVkMusicPossibility, vkid?.login || 0);

    return { mailSpaceCheckStatus, vkMusicCheckStatus };
}

interface OpenVkMusicBundleBuyModal {
    vkid: VKIDInfo;
    tariffInfo: VkMusicBundleTariffInfo;
    onSuccess: () => void;
    onClose: () => void;
    source: VK_MUSIC_BUNDLE_SOURCE;
}

export const openVkMusicBundleBuyModal = async ({ vkid, tariffInfo, onSuccess, onClose, source }: OpenVkMusicBundleBuyModal) => {
    reduxStore.dispatch(VkMusicBundleActions.setVkMusicBundleVKID({ vkid: String(vkid.login) }));
    await productsController.loadProducts();
    const product = ProductsSelectors.getProductById(reduxStore.getState(), tariffInfo.id);
    reduxStore.dispatch(
        openTariffBuy({
            productId: tariffInfo.id,
            onSuccess,
            onClose,
            showBackButton: true,
            onBackButtonClick: () => {
                reduxStore.dispatch(
                    VkMusicBundleActions.buyVkMusicBundle({
                        tariffInfo,
                        vkid,
                        needConfirm: true,
                        source,
                    })
                );
                closeTariffBuyDialog();
                closeVkBuyModalWithSidebar();
            },
            paySource: VK_MUSIC_PAY_SOURCE[source],
            title: `Подписка Mail Space + VK Музыка ${product?.hasTrial ? 'на месяц за 0 ₽' : ''}`,
            featuresList: [
                'Подписка на VK Музыку',
                'Без рекламы в Почте и Облаке',
                'Загрузка файлов до 100 ГБ в Облако',
                'Отправка файлов до 100 ГБ в письмах',
                'Автоудаление папок',
            ],
        })
    );
};

/**
 * Открывает модальное окно с оффером тарифа,
 * выбирая нужные флоу – с явным выбором VKID или без
 */
export const openVkMusicBundleModal = (data: VkMusicBundleModalProps) => {
    const { vkid, tariffInfo, onClose, onClick, source } = data;
    renderVkMusicBundleModal({
        ...data,
        onClick: () => {
            onClick?.();
            if (vkid) {
                reduxStore.dispatch(
                    VkMusicBundleActions.buyVkMusicBundle({
                        tariffInfo,
                        vkid,
                        needConfirm: true,
                        source,
                    })
                );
                closeVkMusicBundleModal();
                return;
            }

            reduxStore.dispatch(
                VkMusicBundleActions.buyVkMusicBundleChoose({
                    baseTariffInfo: tariffInfo,
                    baseVKID: null,
                    onChoose: () => {
                        closeVkMusicBundleModal();
                        onClose?.();
                    },
                    source,
                })
            );
        },
    });
};
